/**
 * The site, node and enrolment routes of the route table (`routes.ts` spreads
 * these into `ROUTES`): PLAN §3's sites and nodes, D21's enrolment, D9's role
 * swap and D21's "Replace node".
 *
 * Cloud only, like the tenancy table: a node does not administer its own site.
 * Three kinds of caller (`RouteDef.auth`):
 *
 *   - a tenant admin (a Cognito user; `tenant.admin`, tenant-wide): the
 *     portal's site administration;
 *   - nobody (`auth: 'none'`): `POST /enrol`, gated by the one-time code it
 *     carries and rate-limited, because the node has no credential yet;
 *   - an enrolled node (`auth: 'node'`, a signed request —
 *     `@acp/cloud-link/nodeSignature.js`): `GET /node/self`.
 *
 * A path id from another tenant answers `not-found`, the same as one that does
 * not exist (D13).
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import type { RouteDef } from './routes.js';
import {
  createSiteEnrolmentCodeRequestSchema,
  createSiteRequestSchema,
  enrolmentCodePathSchema,
  enrolmentCodeResponseSchema,
  enrolRequestSchema,
  enrolResponseSchema,
  listNodesResponseSchema,
  listPendingEnrolmentsResponseSchema,
  listSitesResponseSchema,
  nodePathSchema,
  nodeResponseSchema,
  nodeSelfResponseSchema,
  pendingEnrolmentResponseSchema,
  removeNodeRequestSchema,
  removeNodeResponseSchema,
  replaceNodeRequestSchema,
  replaceNodeResponseSchema,
  sitePathSchema,
  siteResponseSchema,
  swapRolesRequestSchema,
  updateSiteRequestSchema,
} from './sites.js';

export const SITES_ROUTES = {
  createSite: {
    id: 'createSite',
    method: 'POST',
    path: '/sites',
    tag: 'sites',
    summary: 'Create a site',
    description: 'Creates an on-prem site: where a main, a backup and optionally a LAN witness node will run (PLAN §3, D21). Supplying `siteId` makes a retry idempotent: a site with that id is returned as it is. Every tenant also has the automatic cloud site `cloud` (§9.1), which is never created here.',
    params: null,
    query: null,
    body: createSiteRequestSchema,
    response: siteResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listSites: {
    id: 'listSites',
    method: 'GET',
    path: '/sites',
    tag: 'sites',
    summary: 'List sites',
    description: 'Every site of the tenant — the automatic cloud site first — with the studios that name it.',
    params: null,
    query: null,
    body: null,
    response: listSitesResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  getSite: {
    id: 'getSite',
    method: 'GET',
    path: '/sites/{siteId}',
    tag: 'sites',
    summary: 'Get a site',
    description: 'One site, with the studios that name it.',
    params: sitePathSchema,
    query: null,
    body: null,
    response: siteResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  updateSite: {
    id: 'updateSite',
    method: 'PATCH',
    path: '/sites/{siteId}',
    tag: 'sites',
    summary: 'Rename a site',
    description: 'Changes a site’s name. Its id, kind and nodes are unchanged.',
    params: sitePathSchema,
    query: null,
    body: updateSiteRequestSchema,
    response: siteResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  deleteSite: {
    id: 'deleteSite',
    method: 'DELETE',
    path: '/sites/{siteId}',
    tag: 'sites',
    summary: 'Delete a site',
    description: 'Deletes an on-prem site and its revoked nodes’ records. `conflict` while it has an active node (replace or revoke them first) or a studio names it, and for the automatic cloud site.',
    params: sitePathSchema,
    query: null,
    body: null,
    response: siteResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  createEnrolmentCode: {
    id: 'createEnrolmentCode',
    method: 'POST',
    path: '/sites/{siteId}/enrolment-codes',
    tag: 'sites',
    summary: 'Issue an enrolment code',
    description:
      'A one-time code (at most 24 h) and the `docker run … -e ACP_ENROL=<code> …` line that installs a node with it (D21). The code encodes this regional stack’s URL; the cloud keeps only a hash of its secret, so it is shown once. The node enrolling with it becomes main, then backup (D21); `role: witness` issues a witness code instead — its install line runs `--role witness` and the node becomes the site’s LAN witness (D9), discovered by the pair through the site link. Not for the cloud site, whose nodes the cloud-production launcher enrols (phase 6): `validation-failed`.',
    params: sitePathSchema,
    query: null,
    body: createSiteEnrolmentCodeRequestSchema,
    response: enrolmentCodeResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  listPendingEnrolments: {
    id: 'listPendingEnrolments',
    method: 'GET',
    path: '/sites/{siteId}/enrolment-codes',
    tag: 'sites',
    summary: 'List a site’s pending enrolment codes',
    description:
      'The codes issued for the site that no server has used yet — unspent, unexpired, not cancelled — with the role the server will take and when the code expires: the portal’s “waiting for the server to connect…” rows. Never the code or its secret (shown once, when issued). A code leaves this list when a server enrols with it (the site’s change: `node`), expires, or is cancelled.',
    params: sitePathSchema,
    query: null,
    body: null,
    response: listPendingEnrolmentsResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  cancelEnrolmentCode: {
    id: 'cancelEnrolmentCode',
    method: 'DELETE',
    path: '/sites/{siteId}/enrolment-codes/{codeId}',
    tag: 'sites',
    summary: 'Cancel an enrolment code',
    description:
      'Makes a pending code unusable at once: a server presenting it afterwards is refused like any spent code. `not-found` for a code of another site or tenant, or one that no longer exists; `conflict` when a server already enrolled with it (remove that server instead). Cancelling a cancelled code answers it again.',
    params: enrolmentCodePathSchema,
    query: null,
    body: null,
    response: pendingEnrolmentResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  listNodes: {
    id: 'listNodes',
    method: 'GET',
    path: '/sites/{siteId}/nodes',
    tag: 'sites',
    summary: 'List a site’s nodes',
    description: 'Every node of the site, revoked ones included, with its role, link state, last contact, packages, certificate expiry and last health report (PLAN §10.2, §11.1).',
    params: sitePathSchema,
    query: null,
    body: null,
    response: listNodesResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  replaceNode: {
    id: 'replaceNode',
    method: 'POST',
    path: '/sites/{siteId}/nodes/{nodeId}/replace',
    tag: 'sites',
    summary: 'Replace a node',
    description:
      'D21’s “Replace node”: revokes the node — its key is refused from now on, its site link is closed and its DNS name withdrawn — and issues a one-time code for its replacement, which takes over its role. Replacing an already revoked node issues a fresh code.',
    params: nodePathSchema,
    query: null,
    body: replaceNodeRequestSchema,
    response: replaceNodeResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  removeNode: {
    id: 'removeNode',
    method: 'POST',
    path: '/sites/{siteId}/nodes/{nodeId}/remove',
    tag: 'sites',
    summary: 'Remove a node',
    description:
      'Takes a server out of the site for good, without a replacement: revokes it as “Replace” does — its key refused from now on (`node-revoked`), its site link closed, its DNS name withdrawn, its certificate no longer renewed — but issues no code. Removing the main while the site has a backup makes the backup main (`promotedNodeId`; both are told, as by a role swap), so the next server to enrol becomes the backup. `conflict` unless `force` when the node’s last health report says it runs a production (active or standby), or when it is the site’s last main/backup and a production of a studio on the site is active. Removing a revoked node answers it unchanged.',
    params: nodePathSchema,
    query: null,
    body: removeNodeRequestSchema,
    response: removeNodeResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteNode: {
    id: 'deleteNode',
    method: 'DELETE',
    path: '/sites/{siteId}/nodes/{nodeId}',
    tag: 'sites',
    summary: 'Delete a revoked node’s record',
    description:
      'Forgets a replaced or removed server: its record leaves the site’s node list (the portal’s “Replaced servers”). Only a revoked node — `conflict` for an active one (remove or replace it first). Its DNS name is withdrawn again and any certificate record deleted; its health reports expire on their own.',
    params: nodePathSchema,
    query: null,
    body: null,
    response: nodeResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  swapRoles: {
    id: 'swapRoles',
    method: 'POST',
    path: '/sites/{siteId}/swap-roles',
    tag: 'sites',
    summary: 'Swap main and backup',
    description:
      'Makes `mainNodeId` the site’s main node and the current main its backup (D21: “roles can be swapped in the portal”). Both nodes are told over the site link. Idempotent: naming the node that is already main changes nothing. Per-production active/standby roles are unaffected (§8.1). `conflict` unless the site has both an active main and an active backup.',
    params: sitePathSchema,
    query: null,
    body: swapRolesRequestSchema,
    response: listNodesResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  enrol: {
    id: 'enrol',
    method: 'POST',
    path: '/enrol',
    tag: 'node',
    summary: 'Enrol a node (exchange an enrolment code)',
    description:
      'The node end of D21: exchanges a one-time enrolment code for a node identity. Unauthenticated — the code is the credential — and rate-limited per source address; a code is refused after its first use, after it expires, or after five wrong secrets (`unauthorized` for all three, so a guess learns nothing). The node registers its Ed25519 public key (its requests are signed with it from now on) and an X25519 key the cloud seals deliveries to. The first node of a site becomes main, the second backup; a third is refused (`conflict`) unless it enrols as `witness`. The answer carries the stack’s URLs, what the node needs to verify Cognito tokens offline, and the site’s signing key (D20); its local HTTPS certificate follows over the site link (D16).',
    params: null,
    query: null,
    body: enrolRequestSchema,
    response: enrolResponseSchema,
    successStatus: 201,
    permission: null,
    permissionScope: 'tenant',
    auth: 'none',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  getNodeSelf: {
    id: 'getNodeSelf',
    method: 'GET',
    path: '/node/self',
    tag: 'node',
    summary: 'The calling node',
    description: 'The node the request is signed by, as the cloud has it. The simplest signed call: a janitor checks its credentials with it, and a revoked node is refused with `node-revoked` — how a server started again after it was replaced or removed learns so, and why a new code re-enrols it (docs/acp/sites.md).',
    params: null,
    query: null,
    body: null,
    response: nodeSelfResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'node',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
